import { Tokens } from '@/assets/tokens'
import styled from 'styled-components'

export const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Tokens.ColorWhite};
  border-radius: ${Tokens.RadiusInput};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
  min-width: 1080px;

  @media (max-width: 1230px) {
    min-width: 1080px;
    overflow-x: auto;
  }
`

export const TopControls = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${Tokens.ColorBorder};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${Tokens.ColorWhite};
`

export const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

export const SortControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const SortButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${Tokens.ColorBorder};
  border-radius: 6px;
  background-color: ${({ $active }) => ($active ? Tokens.ColorUIPrimary : Tokens.ColorWhite)};
  color: ${({ $active }) => ($active ? Tokens.ColorWhite : Tokens.ColorTextSecondary)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ $active }) => ($active ? Tokens.ColorUIPrimary : Tokens.ColorHover)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const SortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${Tokens.ColorBorder};
  border-radius: 6px;
  background-color: ${Tokens.ColorWhite};
  color: ${Tokens.ColorTextPrimary};
  font-size: 14px;
  cursor: pointer;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${Tokens.ColorUIPrimary};
  }
`

export const TableHeader = styled.div<{ $height?: string }>`
  display: flex;
  border-bottom: 1px solid ${Tokens.ColorBorder};
  background-color: ${Tokens.ColorWhite};
  top: 0;
  height: ${({ $height }) => $height || '60px'};
`

export const HeaderCell = styled.div<{
  $width?: string | number
  $minWidth?: number
  $align?: 'left' | 'center' | 'right'
}>`
  width: ${({ $width }) =>
    typeof $width === 'string' ? $width
    : typeof $width === 'number' ? `${$width}px`
    : 'auto'};
  ${({ $width }) => ($width ? `flex: 0 0 ${typeof $width === 'string' ? $width : `${$width}px`};` : 'flex: 1;')}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth}px;`}
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'center' ? 'center'
    : $align === 'right' ? 'flex-end'
    : 'flex-start'};
  font-weight: 600;
  font-size: 14px;
  color: ${Tokens.ColorTextPrimary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: default;
  user-select: none;

  &:last-child {
    border-right: none;
  }
`

export const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`

export const TableRow = styled.div<{
  $isSelected?: boolean
  $striped?: boolean
  $index?: number
}>`
  display: flex;
  transition: all 0.2s ease;
  background-color: ${({ $isSelected, $striped, $index }) =>
    $isSelected ? Tokens.ColorHover
    : $striped && $index && $index % 2 === 0 ? '#f9fafb'
    : Tokens.ColorWhite};
  border-bottom: 1px solid ${Tokens.ColorBorder};

  &:hover {
    background-color: ${Tokens.ColorHover};
  }
`

export const SelectionContainer = styled.div`
  width: 60px;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const MainRow = styled.div<{ $height?: string }>`
  display: flex;
  min-height: ${({ $height }) => $height || '70px'};
  width: 100%;
  padding: 16px 0;
  min-width: 0;
`

export const ExpansionRow = styled.div`
  width: 100%;
  padding: 8px 0px;
`

export const ExpansionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export const RowCell = styled.div<{
  $width?: string | number
  $minWidth?: number
  $align?: 'left' | 'center' | 'right'
}>`
  width: ${({ $width }) =>
    typeof $width === 'string' ? $width
    : typeof $width === 'number' ? `${$width}px`
    : 'auto'};
  ${({ $width }) => ($width ? `flex: 0 0 ${typeof $width === 'string' ? $width : `${$width}px`};` : 'flex: 1;')}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth}px;`}
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'center' ? 'center'
    : $align === 'right' ? 'flex-end'
    : 'flex-start'};
  font-size: 14px;
  color: ${Tokens.ColorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  width: 0;

  &:last-child {
    border-right: none;
  }
`

export const ActionContainer = styled.div<{
  $width?: string | number
  $minWidth?: number
}>`
  width: ${({ $width }) =>
    typeof $width === 'string' ? $width
    : typeof $width === 'number' ? `${$width}px`
    : 'auto'};
  ${({ $width }) => !$width && 'flex: 1;'}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth}px;`}
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 16px 0;
`

export const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

export const Footer = styled.div`
  padding: 20px;
  border-top: 1px solid ${Tokens.ColorBorder};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${Tokens.ColorTextSecondary};

  span {
    white-space: nowrap;
  }
`

export const StyledSelect = styled.select`
  min-width: 100px;
  height: ${Tokens.HeightInput};
  padding: 0 12px;
  border: 1px solid ${Tokens.ColorBorder};
  border-radius: ${Tokens.RadiusInput};
  background-color: ${Tokens.ColorWhite};
  color: ${Tokens.ColorTextPrimary};
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px;
  padding-right: 32px;

  &:hover:not(:disabled) {
    border-color: ${Tokens.ColorUIPrimary};
  }

  &:focus {
    outline: none;
    border-color: ${Tokens.ColorUIPrimary};
    box-shadow: 0 0 0 2px rgba(1, 195, 141, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${Tokens.ColorBackground};
  }

  option {
    padding: 8px;
  }
`

export const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }

  .tooltip {
    visibility: hidden;
    opacity: 0;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${Tokens.ColorTextPrimary};
    color: white;
    text-align: center;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 12px;
    white-space: nowrap;
    transition: opacity 0.3s;

    &::after {
      content: '';
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: ${Tokens.ColorTextPrimary} transparent transparent transparent;
    }
  }
`
